import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  ExitToApp as LeaveIcon,
  DeleteOutline as DeleteIcon,
  Login as JoinIcon,
  MoreVert as MoreIcon,
  EditCalendarOutlined as NewChatIcon
} from '@mui/icons-material';

const DEPARTMENTS = ['All', 'Engineering', 'Sales', 'Marketing', 'HR', 'General'];

const ChannelList = ({
  channels = [],
  activeChannel,
  selectChannel,
  createChannel,
  joinChannel,
  leaveChannel,
  deleteChannel,
  currentUser,
  loading,
  error,
  refreshChannels,
  searchQuery = ''
}) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState('All');
  const [newChannel, setNewChannel] = useState({
    name: '',
    description: '',
    type: 'public',
    members: []
  });

  const filteredChannels = channels.filter(channel => {
    const matchesSearch = channel.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      (channel.description && channel.description.toLowerCase().includes(searchQuery.toLowerCase()));
    if (!matchesSearch) return false;
    if (selectedDept === 'All') return true;
    const deptLower = selectedDept.toLowerCase();
    return channel.name.toLowerCase().includes(deptLower) || 
      (channel.description && channel.description.toLowerCase().includes(deptLower));
  });

  const canCreateChannel = true;

  const handleCreate = async () => {
    try {
      await createChannel(newChannel);
      setCreateOpen(false);
      setNewChannel({ name: '', description: '', type: 'public', members: [] });
    } catch (err) {
      console.error('Channel creation failed:', err);
      if (err.message === 'Unauthorized') {
        alert('Your session has expired. Please refresh the page.');
      } else {
        alert(err.message);
      }
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = (now - date) / 1000;

    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return date.toLocaleDateString();
  };

  return (
    <Box className="channel-list" sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box
        sx={{
          p: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#ffffff',
          borderBottom: '1px solid #edebe9'
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#242424', fontSize: '15px' }}>
          Channels & Teams
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="New Group / Channel">
            <IconButton
              size="small"
              onClick={() => setCreateOpen(true)}
              sx={{
                bgcolor: '#4b53bc',
                color: '#fff',
                width: 28,
                height: 28,
                '&:hover': { bgcolor: '#3b43a2' }
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Department Filter Pills */}
      <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 0.8, overflowX: 'auto', bgcolor: '#f0f0f0', borderBottom: '1px solid #edebe9', '::-webkit-scrollbar': { display: 'none' } }}>
        {DEPARTMENTS.map(dept => {
          const isSelected = selectedDept === dept;
          return (
            <Chip
              key={dept}
              label={dept}
              size="small"
              onClick={() => setSelectedDept(dept)}
              sx={{
                fontSize: '11px',
                fontWeight: isSelected ? 700 : 500,
                cursor: 'pointer',
                bgcolor: isSelected ? '#4b53bc' : '#ffffff',
                color: isSelected ? '#ffffff' : '#616161',
                border: isSelected ? 'none' : '1px solid #e1dfdd',
                '&:hover': { bgcolor: isSelected ? '#3b43a2' : '#edebe9' }
              }}
            />
          );
        })}
      </Box>

      {/* Channel List */}
      <List className="channel-items" dense sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
        {loading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">Loading channels...</Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>
            <Button size="small" variant="text" onClick={refreshChannels}>Retry</Button>
          </Box>
        )}

        {!loading && !error && channels.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">No channels available.</Typography>
            <Button size="small" sx={{ mt: 1, color: '#4b53bc', fontWeight: 600 }} onClick={() => setCreateOpen(true)}>Create One</Button>
          </Box>
        )}

        {!loading && filteredChannels.map((channel) => {
          const isSelected = activeChannel?.id === channel.id;
          const canDelete = (channel.created_by === currentUser?.id) || currentUser?.role_name === 'Admin' || currentUser?.role_id == 1;

          return (
            <ListItem
              key={channel.id}
              button
              selected={isSelected}
              onClick={() => selectChannel(channel)}
              sx={{
                px: 1.5,
                py: 1,
                mb: 0.5,
                borderRadius: 1.5,
                transition: 'all 0.15s ease',
                bgcolor: isSelected ? '#edebe9' : 'transparent',
                boxShadow: 'none',
                border: isSelected ? '1px solid #d1d1d1' : '1px solid transparent',
                '&.Mui-selected': {
                  bgcolor: '#edebe9',
                },
                '&:hover': {
                  bgcolor: isSelected ? '#edebe9' : '#e8e8e8',
                }
              }}
            >
              <ListItemAvatar sx={{ minWidth: 42 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: isSelected ? '#4b53bc' : '#6264a7',
                    color: '#ffffff',
                    fontSize: 13,
                    fontWeight: 700
                  }}
                >
                  {channel.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Box className="channel-title-row" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: isSelected ? 700 : 500, color: '#242424' }} noWrap>
                      #{channel.name}
                    </Typography>
                    {channel.recent_messages > 0 && channel.my_role && (
                      <Chip
                        size="small"
                        label={channel.recent_messages}
                        sx={{ height: 18, fontSize: '10px', bgcolor: '#4b53bc', color: '#fff', fontWeight: 700 }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" sx={{ color: '#616161' }} noWrap>
                    {channel.member_count} members • {formatTime(channel.last_activity)}
                  </Typography>
                }
              />

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                {canDelete && deleteChannel && (
                  <Tooltip title="Delete Group">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Are you sure you want to permanently delete #${channel.name}?`)) {
                          deleteChannel(channel.id);
                        }
                      }}
                      sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {channel.my_role && (
                  <Tooltip title="Leave Channel">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Leave #${channel.name}?`)) {
                          leaveChannel(channel.id);
                        }
                      }}
                      sx={{ color: '#94a3b8', '&:hover': { color: '#f59e0b' } }}
                    >
                      <LeaveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </ListItem>
          );
        })}
      </List>

      {/* Create Channel Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: 'dialog-paper' }}
      >
        <DialogTitle className="dialog-title">Create New Channel</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Channel Name"
            fullWidth
            value={newChannel.name}
            onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value.replace(/\s+/g, '-').toLowerCase() })}
            placeholder="e.g., engineering-team"
            helperText="Use lowercase letters, numbers, and hyphens"
            className="channel-input"
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={newChannel.description}
            onChange={(e) => setNewChannel({ ...newChannel, description: e.target.value })}
            placeholder="What is this channel about?"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Channel Type</InputLabel>
            <Select
              value={newChannel.type}
              onChange={(e) => setNewChannel({ ...newChannel, type: e.target.value })}
            >
              <MenuItem value="public">
                <Box className="type-option">
                  <PublicIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2">Public</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Anyone can join and view messages
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="private">
                <Box className="type-option">
                  <LockIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2">Private</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Only invited members can access
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className="dialog-actions" sx={{ p: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: '#616161', textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newChannel.name || newChannel.name.length < 3}
            sx={{
              bgcolor: '#4b53bc',
              color: '#fff',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#3b43a2'
              }
            }}
          >
            Create Group / Channel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChannelList;